import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, CheckCircle2, Briefcase, Calendar, MapPin } from 'lucide-react';
import { FALLBACK_REFERENCES, LOGOS } from '../data';

export default function References() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logosList, setLogosList] = useState(LOGOS);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function fetchPage() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/by-slug/da/referencer');
        if (res.ok) {
          const data = await res.json();
          setPageData(data);
        }
      } catch (err) {
        console.error('Error fetching references page:', err);
      }
    }
    fetchPage();
  }, []);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/logos')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch logos');
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => {
            const orderA = a.order !== undefined && a.order !== null ? Number(a.order) : 999;
            const orderB = b.order !== undefined && b.order !== null ? Number(b.order) : 999;
            return orderA - orderB;
          });
          
          const mapped = sorted.map(item => ({
            name: item.name,
            url: Array.isArray(item.logo) && item.logo.length > 0 ? item.logo[0] : ''
          })).filter(item => item.url);
          
          if (mapped.length > 0) {
            setLogosList(mapped);
          }
        }
      })
      .catch(err => {
        console.error('Error fetching logos from CMS:', err);
      });
  }, []);

  const getOptimizedUrl = (url, width = 300) => {
    if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url;
    if (url.includes('/cdn-cgi/image/')) return url;
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  };

  useEffect(() => {
    async function fetchReferences() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/references');
        if (res.ok) {
          const data = await res.json();
          if (data && data.items && data.items.length > 0) {
            // Map CMS items to references shape
            const mapped = data.items.map(item => ({
              id: item.id,
              title: item.title?.da || item.title || '',
              client: item.client?.da || item.client || '',
              project_type: item.project_type?.da || item.project_type || '',
              image: Array.isArray(item.image) ? item.image[0] : item.image,
              description: item.description?.da || item.description || ''
            }));
            setReferences(mapped);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching references from CMS:', err);
      }
      setReferences(FALLBACK_REFERENCES);
      setLoading(false);
    }
    fetchReferences();
  }, []);

  const title = pageData?.title?.da || pageData?.title || "Referencer - CARPA ApS";
  const desc = pageData?.meta_description?.da || pageData?.meta_description || "Se vores udvalgte referencer og projekter inden for PtX, energi og procesindustrien. Vi leverer commissioning, operational readiness og teknisk ledelse.";

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen font-sans antialiased">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[55vh] lg:min-h-[60vh] flex flex-col justify-center pt-16 pb-0 md:pt-24 md:pb-0 overflow-hidden bg-[#0a1520] border-b border-[#0e1f2f]/20">
        {/* Background Grid Pattern & Image Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/afe43705-5832-4975-9507-5d77c6c4492e.jpg"
            alt="Referencer"
            className="h-full w-full object-cover opacity-35 lg:opacity-45 object-center"
          />
          {/* Subtle Technical Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(23,211,214,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(23,211,214,0.04)_1px,transparent_1px)] bg-[size:24px_24px]" />
          {/* Gradient Overlay preserving text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1520] via-[#0a1520]/95 to-transparent hidden lg:block" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1520]/90 via-[#0a1520]/80 to-[#0a1520] lg:hidden" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Title & Process */}
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Action/Process Header Link */}
              <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm font-mono tracking-wider uppercase">
                <span className="text-[#17d3d6] font-bold">
                  {pageData?.hero_badge?.da || pageData?.hero_badge || "Dokumenteret Erfaring"}
                </span>
              </div>

              <div className="space-y-2">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-wide uppercase text-white leading-none">
                  {pageData?.hero_title?.da || pageData?.hero_title || "Referencer"}
                </h1>
                <div className="w-16 h-[3px] bg-[#17d3d6] mt-4" />
              </div>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                {pageData?.hero_description?.da || pageData?.hero_description || "Vi viser vores erfaring og troværdighed gennem konkrees projekter uden at gå på kompromis med fortrolighed."}
              </p>
            </div>

            {/* Right Column: Premium Floating Card with Glow Border */}
            <div className="lg:col-span-5">
              <div className="bg-[#041326]/65 backdrop-blur-md border border-[rgba(23,211,214,0.15)] rounded-xl p-6 lg:p-8 flex flex-col justify-between transition-all duration-300 hover:border-[#17d3d6]/40 shadow-2xl">
                <div>
                  <div className="flex items-center gap-4 pb-4 border-b border-slate-700/40">
                    <div className="w-12 h-12 shrink-0 rounded-full border border-[#17d3d6] flex items-center justify-center text-[#17d3d6]">
                      <Briefcase className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-white text-lg font-bold leading-tight">Vores resultater</h3>
                      <p className="text-xs text-slate-400 font-mono tracking-wider uppercase mt-0.5">CARPA ApS</p>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3.5">
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Gennemførte projekter i PtX & energi
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Tæt samarbejde med førende aktører
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Verificeret funktion og fuld dokumentation
                    </li>
                    <li className="relative pl-6 text-sm text-slate-200">
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[#17d3d6]"></span>
                      Dokumenteret værdiskabelse i praksis
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logos / Trust Section */}
      <section className="border-b border-[#0e1f2f]/10 bg-white py-12 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6">
          
          <style dangerouslySetInnerHTML={{__html: `
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .marquee-container {
              display: flex;
              width: max-content;
              animation: marquee 35s linear infinite;
            }
          `}} />

          <div className="relative w-full overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
            
            <div className="marquee-container flex gap-10 items-center">
              {logosList.map((logo, idx) => (
                <div key={`logo-1-${idx}`} className={`flex items-center justify-center rounded-md px-6 py-3 ring-1 ring-black/5 shrink-0 ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'bg-[#0e1f2f]' : 'bg-white/80'}`}>
                  <img
                    src={getOptimizedUrl(logo.url, 300)}
                    alt={logo.name}
                    className={`h-10 md:h-12 max-w-[150px] object-contain ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'drop-shadow-[0_2px_8px_rgba(255,255,255,0.15)]' : ''}`}
                  />
                </div>
              ))}
              {logosList.map((logo, idx) => (
                <div key={`logo-2-${idx}`} className={`flex items-center justify-center rounded-md px-6 py-3 ring-1 ring-black/5 shrink-0 ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'bg-[#0e1f2f]' : 'bg-white/80'}`}>
                  <img
                    src={getOptimizedUrl(logo.url, 300)}
                    alt={logo.name}
                    className={`h-10 md:h-12 max-w-[150px] object-contain ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'drop-shadow-[0_2px_8px_rgba(255,255,255,0.15)]' : ''}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* References Grid Section */}
      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#0e1f2f]/10 border-t-[#3c8888]" />
            </div>
          ) : (
            <div className="space-y-16 md:space-y-24">
              {references.map((project, idx) => (
                <div 
                  key={project.id || idx}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center border-b border-[#0e1f2f]/10 pb-16 md:pb-24 last:border-0 last:pb-0 ${
                    idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image Column */}
                  <div className={`lg:col-span-6 ${idx % 2 === 1 ? 'lg:order-last' : ''}`}>
                    <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-[#0e1f2f]/10 shadow-md bg-stone-100 group">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60" />
                    </div>
                  </div>

                  {/* Text Column */}
                  <div className="lg:col-span-6 space-y-6">
                    <div className="space-y-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#3c8888]/10 px-3 py-1 text-xs font-bold text-[#3c8888] uppercase tracking-wider">
                        {project.project_type}
                      </span>
                      <h2 className="text-3xl font-extrabold text-[#0e1f2f] tracking-tight leading-tight">
                        {project.title}
                      </h2>
                      <div className="flex items-center gap-2 text-stone-500 text-sm font-medium">
                        <Briefcase className="h-4 w-4 text-stone-400" />
                        <span>Klient: {project.client}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <div 
                      className="text-stone-600 text-base leading-relaxed space-y-4 [&_p]:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: project.description.startsWith('<') ? project.description : `<p>${project.description}</p>` }}
                    />

                    {/* Key takeaways */}
                    <div className="pt-4 border-t border-[#0e1f2f]/10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="h-5 w-5 text-[#3c8888] shrink-0 mt-0.5" />
                        <span className="text-xs text-stone-700 font-medium">Sikker overdragelse og fuld sporbarhed</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="h-5 w-5 text-[#3c8888] shrink-0 mt-0.5" />
                        <span className="text-xs text-stone-700 font-medium">Hands-on teknisk ledelse tæt på anlægget</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="relative py-20 overflow-hidden bg-[#0e1f2f] text-[#f7f8fa] mt-16">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/594540e3-6717-499c-be64-92e37498def2.jpg"
            alt="Kontrolrum"
            className="h-full w-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-[#0e1f2f]/90" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Vil du høre mere om vores erfaringer?
          </h2>
          <p className="mx-auto max-w-xl text-stone-300 text-sm sm:text-base">
            Vi tager gerne en uforpligtende snak om, hvordan vi kan bringe vores tekniske tyngde og praktiske erfaring i spil på jeres anlæg.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-[#3c8888] px-6 py-3.5 text-base font-bold text-white shadow-md hover:bg-[#3c8888]/90 transition-colors border-b-2 border-[#3c8888]"
            >
              Book en afklarende samtale
            </Link>
            <a
              href="tel:+4553636223"
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-white/20 bg-white/10 backdrop-blur-sm px-6 py-3.5 text-base font-bold text-white shadow-sm hover:bg-white/20 transition-all duration-200"
            >
              Ring til Laust
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
